import { ReactNode, ElementType, ComponentPropsWithRef } from 'react'

import { Slot } from '@radix-ui/react-slot'

import { cn } from '@/shared/lib/utils'

export type Variant =
  | 'primary'
  | 'success'
  | 'destructive'
  | 'reward'
  | 'outline'
  | 'secondary'

type EmphasisVariant = 'primary' | 'success' | 'destructive' | 'reward'

export type Size = 'default' | 'sm'

export type ButtonProps<T extends ElementType = 'button'> = {
  as?: T
  asChild?: boolean
  children: ReactNode
  variant?: Variant
  size?: Size
  icon?: boolean
  rounded?: boolean
  loading?: boolean
  toggled?: boolean
} & ComponentPropsWithRef<T>

const layoutBase =
  'inline-flex items-center justify-center gap-2 select-none focus:outline-none focus-visible:ring-2'

const labelTypography = 'font-bold uppercase tracking-wide'

function isEmphasisVariant(variant: Variant): variant is EmphasisVariant {
  return (
    variant === 'primary' ||
    variant === 'success' ||
    variant === 'destructive' ||
    variant === 'reward'
  )
}

function radiusForSize(variant: Variant, size: Size, rounded: boolean): string {
  if (rounded) return 'rounded-full'
  if (variant === 'reward' && size === 'sm') return 'rounded-full'
  return 'rounded-sm'
}

function sizeBlock(
  variant: Variant,
  size: Size,
  icon: boolean,
  rounded: boolean
): string {
  const r = radiusForSize(variant, size, rounded)
  if (icon) {
    if (variant === 'reward' && size === 'sm') {
      return cn(r, 'size-9 shrink-0 p-0 text-[10px] leading-none')
    }
    if (size === 'sm') return cn(r, 'min-h-8 min-w-8 p-0 text-xs')
    return cn(r, 'min-h-11 min-w-11 p-0 text-sm')
  }
  if (variant === 'reward' && size === 'sm') {
    return cn(r, 'min-h-9 min-w-0 px-3 text-[11px] leading-tight')
  }
  if (size === 'sm') {
    return cn(r, 'min-h-8 px-3 text-xs')
  }
  return cn(r, 'min-h-11 px-4 text-sm')
}

function emphasisDisabledClasses(
  variant: Variant,
  size: Size,
  icon: boolean,
  rounded: boolean
): string {
  return cn(
    layoutBase,
    labelTypography,
    sizeBlock(variant, size, icon, rounded),
    'border-2 border-gray-600 bg-gray-600 text-white/90 shadow-none opacity-90 pointer-events-none'
  )
}

function emphasisBaseClasses(
  variant: EmphasisVariant,
  size: Size,
  icon: boolean,
  rounded: boolean
): string {
  const block = sizeBlock(variant, size, icon, rounded)
  const motion = cn(
    'transition-all duration-100',
    'hover:translate-y-0.5',
    'active:translate-y-1',
    'disabled:translate-y-0'
  )

  if (variant === 'primary') {
    return cn(
      layoutBase,
      labelTypography,
      block,
      motion,
      'bg-gradient-to-b from-[#3F80CA] to-[#3C73C0] text-white',
      'shadow-[inset_0_3px_0_0_#3DB2C0,_0_4px_0px_#182A62]',
      'hover:shadow-[inset_0_3px_0_0_#3DB2C0,_0_2px_0px_#182A62]',
      'active:shadow-[inset_0_3px_0_0_#3DB2C0]',
      'disabled:shadow-[inset_0_3px_0_0_#3DB2C0,_0_4px_0px_#182A62]',
      'focus-visible:ring-sky-300/75'
    )
  }

  if (variant === 'success') {
    return cn(
      layoutBase,
      labelTypography,
      block,
      motion,
      'bg-gradient-to-b from-[#29B757] to-[#29BB52] text-white',
      'shadow-[inset_0_3px_0_0_#30D37A,_0_4px_0px_#186218]',
      'hover:shadow-[inset_0_3px_0_0_#30D37A,_0_2px_0px_#186218]',
      'active:shadow-[inset_0_3px_0_0_#30D37A]',
      'disabled:shadow-[inset_0_3px_0_0_#30D37A,_0_4px_0px_#186218]',
      'focus-visible:ring-emerald-300/80'
    )
  }

  if (variant === 'destructive') {
    return cn(
      layoutBase,
      labelTypography,
      block,
      motion,
      'bg-gradient-to-b from-[#CF2D35] to-[#CB212A] text-white',
      'shadow-[inset_0_3px_0_0_#E24137,_0_4px_0px_#6C1B1B]',
      'hover:shadow-[inset_0_3px_0_0_#E24137,_0_2px_0px_#6C1B1B]',
      'active:shadow-[inset_0_3px_0_0_#E24137]',
      'disabled:shadow-[inset_0_3px_0_0_#E24137,_0_4px_0px_#6C1B1B]',
      'focus-visible:ring-red-300/80'
    )
  }

  return cn(
    layoutBase,
    labelTypography,
    block,
    motion,
    'border-2 border-amber-200/95 bg-gradient-to-b from-amber-200 via-yellow-300 to-amber-500 text-[#3d2910]',
    'shadow-[inset_0_2px_0_0_rgba(255,255,255,0.45),0_4px_0_#8b6914]',
    'hover:shadow-[inset_0_2px_0_0_rgba(255,255,255,0.45),0_2px_0_#8b6914]',
    'active:shadow-[inset_0_2px_0_0_rgba(255,255,255,0.45)]',
    'disabled:shadow-[inset_0_2px_0_0_rgba(255,255,255,0.45),0_4px_0_#8b6914]',
    'focus-visible:ring-amber-300/80'
  )
}

function classesForVariant(
  variant: Variant,
  size: Size,
  icon: boolean,
  rounded: boolean,
  disabled: boolean | undefined,
  loading: boolean | undefined,
  toggled: boolean | undefined
): string {
  if (toggled) {
    return cn(
      layoutBase,
      labelTypography,
      sizeBlock(variant, size, icon, rounded),
      'transition-all duration-100',
      'bg-gradient-to-t from-blue-700 to-blue-500 text-white',
      'shadow-[inset_0_4px_0_0_#2457C4]',
      'focus-visible:ring-sky-300/75'
    )
  }

  if (loading && isEmphasisVariant(variant)) {
    return cn(
      emphasisBaseClasses(variant, size, icon, rounded),
      'pointer-events-none animate-pulse opacity-80'
    )
  }

  if (disabled && isEmphasisVariant(variant)) {
    return emphasisDisabledClasses(variant, size, icon, rounded)
  }

  if (variant === 'outline') {
    return cn(
      layoutBase,
      sizeBlock(variant, size, icon, rounded),
      labelTypography,
      'border-2 border-[#c9a227]/80 bg-black/25 text-amber-100/90',
      'hover:bg-black/40 focus-visible:ring-amber-300/70',
      loading && 'pointer-events-none animate-pulse opacity-70',
      disabled && 'opacity-50 pointer-events-none'
    )
  }

  if (variant === 'secondary') {
    return cn(
      layoutBase,
      sizeBlock(variant, size, icon, rounded),
      labelTypography,
      'border-2 border-[#6A81DB]/50 bg-white/55 text-primary-dark',
      'hover:bg-white/75 focus-visible:ring-[#6A81DB]/45',
      loading && 'pointer-events-none animate-pulse opacity-70',
      disabled && 'opacity-50 pointer-events-none'
    )
  }

  return emphasisBaseClasses(variant, size, icon, rounded)
}

export function Button<T extends ElementType = 'button'>({
  as,
  asChild,
  children,
  className,
  variant = 'primary',
  size = 'default',
  rounded = false,
  disabled,
  loading = false,
  icon = false,
  toggled = false,
  ...props
}: ButtonProps<T>) {
  const inert = Boolean(disabled || loading)
  const variantClasses = classesForVariant(
    variant,
    size,
    icon,
    rounded,
    disabled,
    loading,
    toggled
  )
  const mergedClassName = cn(
    variantClasses,
    className,
    inert && 'cursor-not-allowed'
  )

  if (asChild) {
    return (
      <Slot
        {...(props as Record<string, unknown>)}
        className={mergedClassName}
        {...(inert ? { 'aria-disabled': true, tabIndex: -1 } : {})}
        {...(loading ? { 'aria-busy': true } : {})}
      >
        {children}
      </Slot>
    )
  }

  const Component = as || 'button'
  const passDisabled = !as || as === 'button'

  return (
    <Component
      {...props}
      {...(passDisabled ? { disabled: inert } : {})}
      {...(!passDisabled && inert ? { 'aria-disabled': true as const } : {})}
      {...(loading ? { 'aria-busy': true as const } : {})}
      className={cn(
        mergedClassName,
        !passDisabled && inert && 'pointer-events-none'
      )}
    >
      {children}
    </Component>
  )
}
